import { useCallback, useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useStore } from "../components/contexts/StoreContext";
import { resolveCloudMevEnv } from "../cloud-mev/cloudMevEnv";

const STORAGE_KEY = "posWindowModules";

export type PosEnv = "DEV" | "ESSAI" | "PROD";

type ModuleFlags = Partial<Record<PosEnv, boolean>>;
type ModuleMap = Record<string, ModuleFlags>;

let current: ModuleMap = readStorage();
const listeners = new Set<(map: ModuleMap) => void>();

function readStorage(): ModuleMap {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as ModuleMap;
  } catch {
    return {};
  }
}

function publish(map: ModuleMap) {
  current = map;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // The register keeps the in-memory map when storage is unavailable.
  }
  listeners.forEach((listener) => listener(map));
}

function asEnv(value: string): PosEnv {
  if (value === "ESSAI" || value === "PROD") return value;
  return "DEV";
}

function flagOn(map: ModuleMap, key: string, env: PosEnv): boolean {
  const row = map[key];
  if (!row || typeof row !== "object") return true;
  return row[env] !== false;
}

export function moduleOn(map: ModuleMap, key: string, envirn: string, parentKey?: string): boolean {
  const env = asEnv(envirn);
  if (parentKey && !flagOn(map, parentKey, env)) return false;
  return flagOn(map, key, env);
}

export function watchPosModules(): () => void {
  return onSnapshot(
    doc(db, "systemConfig", "posWindow"),
    (snap) => {
      const modules = snap.exists() ? snap.data()?.modules : null;
      publish(modules && typeof modules === "object" ? modules as ModuleMap : {});
    },
    () => {
      // Keep the last saved map while the register is offline.
    }
  );
}

export function subscribePosModules(listener: (map: ModuleMap) => void): () => void {
  listener(current);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function usePosModuleOn(): (key: string, parentKey?: string) => boolean {
  const { selectedStoreId, currentStore } = useStore();
  const [map, setMap] = useState<ModuleMap>(current);
  const [envirn, setEnvirn] = useState<PosEnv>("DEV");

  useEffect(() => subscribePosModules(setMap), []);

  useEffect(() => {
    const sync = () => {
      setEnvirn(resolveCloudMevEnv((currentStore as { cloudMevEnvirn?: string } | null)?.cloudMevEnvirn, selectedStoreId));
    };
    sync();
    window.addEventListener("cloud-mev-envirn", sync);
    return () => window.removeEventListener("cloud-mev-envirn", sync);
  }, [selectedStoreId, currentStore]);

  return useCallback(
    (key: string, parentKey?: string) => moduleOn(map, key, envirn, parentKey),
    [map, envirn]
  );
}
